import React, { useState } from "react";
import * as XLSX from "xlsx";
import { API_BASE } from "../api/config";

import LocationSelect from "../components/LocationSelect";
import "./ExcelInventoryImportPanel.css";

const IMPORT_API_BASE = API_BASE || "https://178.128.80.46.sslip.io";


function todayKey() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function toNumber(value) {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function clean(value) {
  return String(value ?? "").trim();
}

function isSizeHeader(value) {
  const v = clean(value).toUpperCase();
  return /^(XXS|XS|S|M|L|XL|XXL|XXXL)$/.test(v) || /^[0-9]{2}$/.test(v);
}

function excelCellText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function excelCellNumber(value) {
  if (value === null || value === undefined) return 0;
  const text = String(value).replace(/,/g, "").replace(/[^\d.-]/g, "").trim();
  if (text === "") return 0;
  const n = Number(text);
  return Number.isFinite(n) ? n : 0;
}

function excelCellIsBlank(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function excelHeaderKey(value) {
  return excelCellText(value).toLowerCase().replace(/[\s_\-\/]+/g, "");
}

function normalizeProductCode(value) {
  const text = excelCellText(value).toUpperCase();

  // Fix codes like 10410-030-001-011 -> 10410-030-001-01
  // Only applies to numeric dashed product codes.
  const m = text.match(/^(\d{5}-\d{3}-\d{3}-)(\d{2})\d$/);
  if (m) return `${m[1]}${m[2]}`;

  return text;
}
function parseInventorySheet(sheetRows) {
  if (!sheetRows.length) return [];

  const header = sheetRows[0] || [];

  const codeIndex = header.findIndex((x) =>
    ["code", "productcode", "product", "barcode", "itemcode"].includes(excelHeaderKey(x))
  );

  const colorIndex = header.findIndex((x) =>
    ["color", "colour"].includes(excelHeaderKey(x))
  );

  const priceIndex = header.findIndex((x) =>
    ["price", "sellingprice", "sellingpricemmk", "saleprice", "unitprice"].includes(excelHeaderKey(x))
  );

  if (codeIndex < 0) {
    throw new Error("Excel header ထဲမှာ Code column မတွေ့ပါ");
  }

  if (colorIndex < 0) {
    throw new Error("Excel header ထဲမှာ color column မတွေ့ပါ");
  }

  const sizeColumns = header
    .map((title, index) => ({ title, index }))
    .filter(({ title, index }) => {
      if (index === codeIndex || index === colorIndex || index === priceIndex) return false;

      // IMPORTANT: size header 0 ကို blank မဟုတ်ဘဲ valid size အဖြစ်ယူမယ်
      if (title === null || title === undefined) return false;
      if (String(title).trim() === "") return false;

      return true;
    });

  if (sizeColumns.length === 0) {
    throw new Error("Excel header ထဲမှာ size columns မတွေ့ပါ");
  }

  const out = [];

  for (let r = 1; r < sheetRows.length; r += 1) {
    const row = sheetRows[r] || [];

    const productCode = normalizeProductCode(row[codeIndex]);
    const color = excelCellText(row[colorIndex]).toUpperCase() || "-";

    // price column မပါရင် 0, price blank ဖြစ်ရင်လည်း 0
    const price = priceIndex >= 0 ? excelCellNumber(row[priceIndex]) : 0;

    if (!productCode) continue;

    for (const col of sizeColumns) {
      // size 0 ဖြစ်ရင် "0" အဖြစ်ယူမယ်
      const size = excelCellText(col.title).toUpperCase();

      if (size === "") continue;

      const qtyCell = row[col.index];

      // blank qty cell ကိုတော့ import row မလုပ်ဘူး
      // ဒါပေမယ့် 0 ကို blank မဟုတ်ဘဲ valid qty အဖြစ်ယူမယ်
      if (excelCellIsBlank(qtyCell)) continue;

      const qty = excelCellNumber(qtyCell);

      out.push({
        productCode,
        code: productCode,
        color,
        size,
        qty,
        excelQty: qty,
        sellingPriceMmk: price,
        price,
        sourceRow: r + 1,
        sourceColumn: excelCellText(col.title),
      });
    }
  }

  return out;
}

export default function ExcelInventoryImportPanel({ onImported }) {
  const [locationCode, setLocationCode] = useState("GMP");
  const [brand, setBrand] = useState("PRETTYFIT");
  const [movementDate, setMovementDate] = useState(todayKey());
  const [unitCostMmk, setUnitCostMmk] = useState("");
  const [batchNo, setBatchNo] = useState(`ERP-EXCEL-GMP-PRETTYFIT-${todayKey()}`);
  const [importMode, setImportMode] = useState("SET");

  const [rows, setRows] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewSummary, setPreviewSummary] = useState(null);

  const [fileName, setFileName] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function readExcel(file) {
    try {
      setError("");
      setMessage("");
      setPreviewRows([]);
      setPreviewSummary(null);
      setFileName(file.name);

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

      const sheetRows = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        defval: "",
      });

      const parsed = parseInventorySheet(sheetRows);

      setRows(parsed);
      setMessage(`Excel read complete: ${parsed.length} size rows found ✅`);
    } catch (err) {
      setRows([]);
      setError(err.message || "Excel read failed");
    }
  }

  async function sendImport(dryRun) {
    const endpoint = dryRun
      ? "/api/erp/stock-excel-import-erp-only/dry-run"
      : "/api/erp/stock-excel-import-erp-only";

    const res = await fetch(`${IMPORT_API_BASE}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        locationCode,
        brand,
        movementDate,
        unitCostMmk: Number(unitCostMmk || 0),
        batchNo,
        importMode,
        dryRun,
        rows,
      }),
    });

    const rawText = await res.text();

    let data = null;
    try {
      data = rawText ? JSON.parse(rawText) : {};
    } catch (err) {
      throw new Error(
        "API did not return JSON. Check API_BASE. Response: " +
          rawText.slice(0, 200)
      );
    }

    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Import request failed");
    }

    return data;
  }

  async function dryRunPreview() {
    try {
      setPreviewing(true);
      setError("");
      setMessage("");

      if (rows.length === 0) {
        throw new Error("Excel rows မရှိပါ");
      }

      if (!batchNo.trim()) {
        throw new Error("Batch No ထည့်ပါ");
      }

      const data = await sendImport(true);

      setPreviewSummary(data.summary || null);
      setPreviewRows(data.rows || []);
      setMessage(data.message || "Dry run finished ✅");
    } catch (err) {
      setError(err.message || "Dry run failed");
    } finally {
      setPreviewing(false);
    }
  }

  async function importRows() {
    try {
      setSaving(true);
      setError("");
      setMessage("");

      if (rows.length === 0) {
        throw new Error("Import လုပ်စရာ row မရှိပါ");
      }

      const ok = window.confirm(
        `${locationCode} ထဲကို Excel qty import လုပ်မယ်။\n\nMode: ${importMode}\nBatch: ${batchNo}\n\nSQL ERP ledger movement ပဲ insert လုပ်မှာပါ။ Firebase မထိပါ။ Continue?`
      );

      if (!ok) return;

      const data = await sendImport(false);

      setPreviewSummary(data.summary || null);
      setPreviewRows(data.rows || []);
      if (!(data?.summary?.dryRun)) {
        await fetch(`${IMPORT_API_BASE}/api/erp/inventory-catalog-upsert`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            locationCode,
            brand,
            batchNo,
            rows,
          }),
        });
      }

      setMessage(`Imported to ERP Ledger ✅ Ledger rows: ${data.summary?.ledgerRows || 0}, IN: ${data.summary?.totalInQty || 0}, OUT: ${data.summary?.totalOutQty || 0}, Valid Excel rows: ${data.summary?.validRows || 0}`);

      setRows([]);
      setPreviewRows([]);
      setPreviewSummary(null);
      setFileName("");
      setFileInputKey((key) => key + 1);

      onImported?.();
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="excelInventory">
      <div className="excelInventory__head">
        <div>
          <span>ERP LEDGER ONLY</span>
          <h2>Excel Stock Qty → ERP LEDGER ONLY</h2>
          <p>
            Excel qty ကို selected shop/warehouse ရဲ့ SQL ERP stock movement ledger ထဲပဲ IN/OUT history ထည့်မယ်။ Firebase မထိပါ။
          </p>
        </div>
      </div>

      {message && <div className="excelInventoryMsg excelInventoryMsg--ok">{message}</div>}
      {error && <div className="excelInventoryMsg excelInventoryMsg--error">{error}</div>}

      <div className="excelInventoryGrid">
        <label>
          <span>Import Location</span>
          <LocationSelect
            value={locationCode}
            onChange={setLocationCode}
            mode="all"
            includeAll={false}
          />
        </label>

        <label>
          <span>Brand</span>
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="PRETTYFIT">PRETTYFIT</option>
            <option value="HANGTEN">HANGTEN</option>
          </select>
        </label>

        <label>
          <span>Import Mode</span>
          <select value={importMode} onChange={(e) => setImportMode(e.target.value)}>
            <option value="SET">SET exact qty</option>
            <option value="ADD">ADD to existing qty</option>
          </select>
        </label>

        <label>
          <span>Date</span>
          <input
            type="date"
            value={movementDate}
            onChange={(e) => setMovementDate(e.target.value)}
          />
        </label>

        <label>
          <span>Unit Cost MMK</span>
          <input
            type="number"
            value={unitCostMmk}
            onChange={(e) => setUnitCostMmk(e.target.value)}
            placeholder="optional"
          />
        </label>

        <label>
          <span>Batch No</span>
          <input value={batchNo} onChange={(e) => setBatchNo(e.target.value)} />
        </label>

        <label>
          <span>Excel File</span>
          <input
            key={fileInputKey}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) readExcel(file);
            }}
          />
        </label>
      </div>

      <div className="excelInventoryWarning">
        <b>SET exact qty</b> = SQL ERP qty ကို Excel qty အတိုင်းထားမယ်။ SQL ledger မှာ Current ERP Qty နဲ့ Excel Qty difference ကို IN/OUT movement ထည့်မယ်။{" "}
        <b>ADD</b> = SQL ERP qty ကိုပေါင်းမယ်။ SQL ledger မှာ Excel qty ကို IN အနေနဲ့ထည့်မယ်။ Import မလုပ်ခင် Dry Run Preview အရင်နှိပ်ပါ။
      </div>

      {previewSummary && (
        <div className="excelInventorySummary">
          <div>
            <span>Location</span>
            <b>{previewSummary.locationCode}</b>
          </div>
          <div>
            <span>Mode</span>
            <b>{previewSummary.importMode}</b>
          </div>
          <div>
            <span>Valid Rows</span>
            <b>{previewSummary.validRows}</b>
          </div>
          <div>
            <span>SQL Ledger Rows</span>
            <b>{previewSummary.ledgerRows}</b>
          </div>
          <div>
            <span>Firebase Paths</span>
            <b>{previewSummary.firebaseUpdatePaths}</b>
          </div>
          <div>
            <span>Duplicate Batch</span>
            <b>{previewSummary.duplicateCount}</b>
          </div>
        </div>
      )}

      <div className="excelInventoryActions">
        <div>
          Excel rows: <b>{rows.length}</b> | Preview rows: <b>{previewRows.length}</b>
        </div>

        <div className="excelInventoryActionBtns">
          <button onClick={dryRunPreview} disabled={previewing || rows.length === 0}>
            {previewing ? "Previewing..." : "Dry Run Preview"}
          </button>

          <button
            className="excelInventoryImportBtn"
            onClick={importRows}
            disabled={saving || rows.length === 0}
          >
            {saving ? "Importing..." : "Import ERP Ledger"}
          </button>
        </div>
      </div>

      {(previewRows.length > 0 || rows.length > 0) && (
        <div className="excelPreviewWrap">
          <table className="excelPreviewTable">
            <thead>
              <tr>
                <th>Code</th>
                <th>Color</th>
                <th>Size</th>
                <th>Old SQL ERP qty</th>
                <th>Current ERP Qty</th>
                <th>Excel Qty</th>
                <th>New SQL ERP qty</th>
                <th>SQL Ledger Qty</th>
                <th>Price</th>
              </tr>
            </thead>

            <tbody>
              {(previewRows.length > 0 ? previewRows : rows).slice(0, 120).map((row, index) => (
                <tr key={`${row.productCode}-${row.color}-${row.size}-${index}`}>
                  <td><b>{row.productCode}</b></td>
                  <td>{row.color}</td>
                  <td>{row.size}</td>
                  <td>{row.oldFirebasePcs ?? row.oldPcs ?? "-"}</td>
                  <td>{row.currentErpQty ?? "-"}</td>
                  <td><b>{row.excelQty ?? row.qty}</b></td>
                  <td><b>{row.newFirebasePcs ?? row.newPcs ?? "-"}</b></td>
                  <td>{row.ledgerQty ?? "-"}</td>
                  <td>{Number(row.sellingPriceMmk || 0).toLocaleString()} Ks</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}









